import React, { useEffect, useRef, useState } from "react";
import { useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, TextareaField, SelectField } from "@/components/kit/fields";
import { FileUpload } from "@/components/kit/FileUpload";
import { toast } from "sonner";

const empty = {
  name: "", vertical: "", image_url: "", description: "", notes: "",
};

export function ProductDrawer({ open, item, onOpenChange }) {
  const opts = useOptions();
  const { create, update } = useResourceMutations("products");
  const [form, setForm] = useState(empty);
  const baseline = useRef(JSON.stringify(empty));
  const isEdit = !!item?.id;

  useEffect(() => { if (open) { const init = item ? { ...empty, ...item } : { ...empty }; setForm(init); baseline.current = JSON.stringify(init); } }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== baseline.current;

  const save = async () => {
    if (!form.name) return toast.error("Product name is required");
    if (isEdit) await update.mutateAsync({ id: item.id, body: form });
    else await create.mutateAsync(form);
    onOpenChange(false);
  };

  // Verticals come from tenant settings (Settings -> Verticals). Keep any legacy
  // stored value selectable so editing an old product never loses its vertical.
  const verticalOpts = Array.from(new Set([...(opts.verticals || []), form.vertical].filter(Boolean)));

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit Product" : "New Product"}
      subtitle={isEdit ? form.name : "Create a product / brand"} size="lg" dirty={dirty}
      onSave={save} saving={create.isPending || update.isPending} saveLabel={isEdit ? "Save changes" : "Create"} testId="product-drawer">
      <div className="space-y-5">
        <TextField label="Product name" value={form.name} onChange={(v) => set("name", v)} testId="product-name" />

        <SelectField label="Vertical" value={form.vertical} onChange={(v) => set("vertical", v)}
          options={verticalOpts} testId="product-vertical" placeholder="Select vertical..."
          hint="Verticals are managed in Settings → Verticals." />

        <FileUpload label="Product / brand image" value={form.image_url} onChange={(v) => set("image_url", v)}
          kind="product" aspect="wide" testId="product-image" hint="Used in offer cards." />

        <TextareaField label="Description" value={form.description} onChange={(v) => set("description", v)} rows={4} />
        <TextareaField label="Notes" value={form.notes} onChange={(v) => set("notes", v)} rows={2} />
      </div>
    </FormDrawer>
  );
}
